import React, { useState } from 'react';
import { EmployeeInviteRequest } from '../../types/auth';
import { showWarning } from '../../utils/notification';

interface EmployeeInviteFormProps {
    onSubmit: (data: EmployeeInviteRequest) => void;
    onCancel: () => void;
    isSubmitting: boolean;
}

export const EmployeeInviteForm: React.FC<EmployeeInviteFormProps> = ({ onSubmit, onCancel, isSubmitting }) => {
    const [formData, setFormData] = useState<EmployeeInviteRequest>({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        position: '',
        isSignatory: false
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Проверяем, что указан хотя бы один контакт
        if (!formData.phoneNumber && !formData.email) {
            showWarning('Необходимо указать контакт', 'Укажите телефон или email для приглашения сотрудника');
            return;
        }

        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Фамилия */}
                <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                        Фамилия *
                    </label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Введите фамилию"
                    />
                </div>

                {/* Имя */}
                <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                        Имя *
                    </label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Введите имя"
                    />
                </div>


                {/* Должность */}
                <div>
                    <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                        Должность *
                    </label>
                    <input
                        type="text"
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Введите должность"
                    />
                </div>

                {/* Телефон */}
                <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                        Номер телефона
                    </label>
                    <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+7XXXXXXXXXX"
                    />
                </div>

                {/* Email */}
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="example@email.com"
                    />
                </div>
            </div>

            {/* Подписант */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isSignatory"
                        name="isSignatory"
                        checked={formData.isSignatory}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isSignatory" className="ml-2 block text-sm text-gray-900">
                        <span className="font-medium">Подписант</span>
                        <span className="text-gray-600 ml-1">(может подписывать договоры)</span>
                    </label>
                </div>
            </div>

            {/* Информация о контактах */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <strong>Важно:</strong> Необходимо указать хотя бы один контакт (телефон или email) для отправки приглашения.
                </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                    Отмена
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isSubmitting ? 'Отправка приглашения...' : 'Отправить приглашение'}
                </button>
            </div>
        </form>
    );
};
